/**
 * 喜樂 Hei Lok — postal code → delivery zone
 *
 * WHAT THIS DOES
 * Reads the customer's Singapore postal code out of the DPO "Postal Code" field,
 * derives which delivery zone they are in, and writes that zone into the DPO
 * "Your Area" dropdown. DPO's own visibility rules do everything after that:
 * the zone value reveals the correct Date of Delivery dropdown.
 *
 * That is the ENTIRE job. Dates, timeslots and which zone sees which dates are
 * configured in the DPO admin, not here, so Nic can change the schedule without
 * touching this file.
 *
 * HOW IT FINDS THE FIELDS
 * DPO assigns its own element IDs (select_1002, dynamic_option_id_1002, …) and
 * they change if options are rebuilt. So we hook on CSS classes instead, set
 * once in the DPO admin on each option's "CSS Class" setting:
 *     Postal Code field  → hl-postal
 *     Your Area field    → hl-zone
 *
 * WHY THE DOUBLE EVENT
 * DPO binds its dependent-option cascade through jQuery. Setting .value and
 * firing only a native change event updates the box but does NOT reveal the
 * dependent date dropdown. Both events are required. (Verified on the Mansa
 * configurator, 2026-05.)
 */
(function () {
  'use strict';

  /**
   * The Area field is derived, never chosen, so the customer must not be able to
   * touch it — and it stays out of the way until a postal code resolves. DPO puts
   * the CSS class on the wrapping <td>, so that is what we hide; the row collapses
   * to zero height on its own.
   *
   * Injected from here rather than pasted into DPO's CSS Adjustment box so this
   * file stays the single place the behaviour lives.
   */
  var CSS =
    '.hl-zone{display:none}' +
    '.hl-zone.hl-zone--shown{display:table-cell}' +
    '.hl-zone select{pointer-events:none;background-image:none;opacity:.7}' +
    '.hl-zone-status{margin:.35rem 0 0;font-size:.85em;opacity:.75}' +
    '.hl-zone-status--error{color:#b3261e;opacity:1}';

  var style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);

  // Singapore postal sectors are the first 2 digits of the 6-digit code.
  // Ranges are inclusive. Sector 74 is not issued and is rejected as invalid.
  var ZONES = [
    ['South',   [[1, 10], [14, 16]]],
    ['West',    [[11, 13], [24, 27], [58, 71]]],
    ['Central', [[17, 23], [28, 37], [56, 57]]],
    ['East',    [[38, 55], [81, 82]]],
    ['North',   [[72, 73], [75, 80]]]
  ];

  // Shown back to the customer so a typo is obvious before they pick a date.
  var LOCALITY = {
    1: 'Raffles Place', 2: 'Raffles Place', 3: 'Marina', 4: 'Marina', 5: 'Marina',
    6: 'Marina', 7: 'Tanjong Pagar', 8: 'Chinatown', 9: 'Telok Blangah',
    10: 'HarbourFront', 11: 'Pasir Panjang', 12: 'West Coast', 13: 'Clementi',
    14: 'Queenstown', 15: 'Tiong Bahru', 16: 'Redhill', 17: 'City Hall',
    18: 'Bugis', 19: 'Beach Road', 20: 'Little India', 21: 'Farrer Park',
    22: 'Orchard', 23: 'River Valley', 24: 'Tanglin', 25: 'Ardmore',
    26: 'Bukit Timah', 27: 'Holland', 28: 'Watten Estate', 29: 'Novena',
    30: 'Thomson', 31: 'Balestier', 32: 'Toa Payoh', 33: 'Toa Payoh',
    34: 'Macpherson', 35: 'Braddell', 36: 'Potong Pasir', 37: 'Bidadari',
    38: 'Geylang', 39: 'Eunos', 40: 'Ubi', 41: 'Paya Lebar', 42: 'Katong',
    43: 'Joo Chiat', 44: 'Marine Parade', 45: 'East Coast', 46: 'Bedok',
    47: 'Siglap', 48: 'Chai Chee', 49: 'Changi', 50: 'Loyang', 51: 'Tampines',
    52: 'Pasir Ris', 53: 'Serangoon Gardens', 54: 'Hougang', 55: 'Sengkang',
    56: 'Bishan', 57: 'Ang Mo Kio', 58: 'Upper Bukit Timah', 59: 'Ulu Pandan',
    60: 'Jurong', 61: 'Jurong', 62: 'Boon Lay', 63: 'Tuas', 64: 'Jurong',
    65: 'Hillview', 66: 'Dairy Farm', 67: 'Bukit Batok', 68: 'Choa Chu Kang',
    69: 'Lim Chu Kang', 70: 'Tengah', 71: 'Kranji', 72: 'Woodlands',
    73: 'Admiralty', 75: 'Yishun', 76: 'Sembawang', 77: 'Upper Thomson',
    78: 'Springleaf', 79: 'Seletar', 80: 'Yio Chu Kang', 81: 'Flora Road',
    82: 'Punggol'
  };

  function zoneForSector(sector) {
    for (var i = 0; i < ZONES.length; i++) {
      var ranges = ZONES[i][1];
      for (var j = 0; j < ranges.length; j++) {
        if (sector >= ranges[j][0] && sector <= ranges[j][1]) return ZONES[i][0];
      }
    }
    return null;
  }

  /**
   * DPO option <option> values are internal DPO ids, not the titles, so we match
   * on the visible text. DPO also appends price suffixes like " (+$5.00)", hence
   * the prefix match rather than equality.
   */
  function optionValueByLabel(select, label) {
    var wanted = label.toLowerCase();
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].text.trim().toLowerCase().indexOf(wanted) === 0) {
        return select.options[i].value;
      }
    }
    return null;
  }

  function setZone(select, zone) {
    var value = zone ? optionValueByLabel(select, zone) : '';
    select.value = value === null ? '' : value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    if (window.jQuery) window.jQuery(select).trigger('change');
  }

  function statusNode(input) {
    var el = input.parentNode.querySelector('.hl-zone-status');
    if (!el) {
      el = document.createElement('p');
      el.className = 'hl-zone-status';
      input.parentNode.appendChild(el);
    }
    return el;
  }

  function handle(input, zoneSelect) {
    var digits = (input.value || '').replace(/\D/g, '').slice(0, 6);
    if (digits !== input.value) input.value = digits;

    var status = statusNode(input);
    var cell = zoneSelect.closest ? zoneSelect.closest('.hl-zone') : null;

    function reveal(on) {
      if (cell) cell.classList.toggle('hl-zone--shown', on);
    }

    // If DPO has hidden the postal field itself — e.g. the customer switched to
    // self-collection — the derived Area must go with it.
    if (input.offsetParent === null) {
      setZone(zoneSelect, null);
      reveal(false);
      status.textContent = '';
      status.className = 'hl-zone-status';
      return;
    }

    if (digits.length < 6) {
      setZone(zoneSelect, null);
      reveal(false);
      status.textContent = '';
      status.className = 'hl-zone-status';
      return;
    }

    var sector = parseInt(digits.slice(0, 2), 10);
    var zone = zoneForSector(sector);

    if (!zone) {
      setZone(zoneSelect, null);
      reveal(false);
      status.textContent = 'That does not look like a Singapore postal code. Please check it.';
      status.className = 'hl-zone-status hl-zone-status--error';
      return;
    }

    // The Area field now shows the zone itself, so the status line only carries
    // the locality — which is what makes a mistyped code obvious.
    status.textContent = LOCALITY[sector] || '';
    status.className = 'hl-zone-status';
    setZone(zoneSelect, zone);
    reveal(true);
  }

  function wire() {
    var input = document.querySelector('.hl-postal input, input.hl-postal');
    var zoneSelect = document.querySelector('.hl-zone select, select.hl-zone');
    if (!input || !zoneSelect || input.dataset.hlWired) return false;

    input.dataset.hlWired = '1';
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('autocomplete', 'postal-code');
    input.setAttribute('maxlength', '6');

    var run = function () { handle(input, zoneSelect); };
    input.addEventListener('input', run);
    input.addEventListener('blur', run);

    // Any other DPO field changing can change whether the postal field is shown
    // at all (delivery vs self-collection), so re-evaluate. Ignore our own writes
    // to the zone select, which would otherwise recurse.
    var root = document.getElementById('itoris_dynamicproductoptions');
    if (root) {
      root.addEventListener('change', function (e) {
        if (e.target !== zoneSelect) run();
      }, true);
    }

    run(); // in case the browser restored a value on back-navigation
    return true;
  }

  // DPO injects its fields client-side after page load, and re-injects when the
  // theme replaces the product form. Keep watching rather than running once.
  if (!wire()) {
    var observer = new MutationObserver(function () { wire(); });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    // Stop watching after 30s so we are not observing the DOM for the whole session.
    setTimeout(function () { observer.disconnect(); }, 30000);
  }
})();
